import React, { useEffect, useMemo, useState } from 'react';
import { Formik, Form, FieldArray, FormikHelpers } from 'formik';
import { object, array, string } from 'yup';
import { useTranslation } from 'react-i18next';

import { IMetaField } from '../../types/meta-fields-types';
import SpinnerLoader from '../SpinnerLoader/SpinnerLoader';
import { FieldInputV2 } from '../FieldInput/FieldInputV2';
import NewFieldForm from '../DefaultMetaFields/components/NewFieldForm';
import { RANGE_META_FIELD } from './constants/common';

interface MetaFieldsV2Props {
  id: string;
  buttons?: React.ReactElement[];
  createMetaField: (payload: { id: string; data: { key: string; value: string } }) => Promise<IMetaField>;
  deleteMetaField: (payload: { id: string; metaFieldId: string }) => Promise<void>;
  metaFields: IMetaField[] | null;
  onChangeListMetaFields: (list: IMetaField[]) => void;
  title: string;
  updateListMetaFields: (payload: { id: string; list: IMetaField[] }) => Promise<void>;
  getMetaFields: (id: string) => Promise<IMetaField[]>;
  isCustomLoading?: boolean;
}
interface FormValues {
  metaFields: IMetaField[];
}

const MetaFieldsV2: React.FC<MetaFieldsV2Props> = ({
  id,
  buttons,
  createMetaField,
  deleteMetaField,
  metaFields: initialMetaFields,
  onChangeListMetaFields,
  title,
  updateListMetaFields,
  getMetaFields,
  isCustomLoading = false,
}) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [fields, setFields] = useState<IMetaField[] | null>(initialMetaFields);

  const isViewLoading = useMemo(() => isLoading || isCustomLoading, [isCustomLoading, isLoading]);

  useEffect(() => {
    let isCancelled = false;

    const fetchAndSetFields = async () => {
      setIsLoading(true);
      setFields(null);
      try {
        const fetchedFields = await getMetaFields(id);
        if (isCancelled) return;
        setFields(fetchedFields);
        onChangeListMetaFields(fetchedFields);
      } catch (error) {
        console.error('Failed to fetch meta fields:', error);
        if (!isCancelled) {
          setFields([]);
        }
      } finally {
        if (!isCancelled) {
          setIsLoading(false);
        }
      }
    };

    fetchAndSetFields();

    return () => {
      isCancelled = true;
    };
    // id changing always means "different entity" - always refetch rather than
    // trusting a possibly-stale cached list from a previously viewed entity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const validationSchema = object().shape({
    metaFields: array().of(
      object().shape({
        key: string()
          .required('Key is required')
          .min(RANGE_META_FIELD.MIN, `Key must be at least ${RANGE_META_FIELD.MIN} characters`)
          .max(RANGE_META_FIELD.MAX, `Key must be max ${RANGE_META_FIELD.MAX} characters`),
        value: string()
          .required('Value is required')
          .min(RANGE_META_FIELD.MIN, `Value must be at least ${RANGE_META_FIELD.MIN} characters`)
          .max(RANGE_META_FIELD.MAX, `Value must be max ${RANGE_META_FIELD.MAX} characters`),
      })
    ),
  });

  const handleUpdateAll = async (values: FormValues, { setSubmitting }: FormikHelpers<FormValues>) => {
    try {
      await updateListMetaFields({ id, list: values.metaFields });
      onChangeListMetaFields(values.metaFields);
    } catch (error) {
      console.error('Failed to update meta fields:', error);
    } finally {
      setSubmitting(false);
    }
  };

  if (fields === null) {
    return (
      <div className="mt-20 flex h-64 w-full items-center justify-center rounded-sm border border-stroke bg-white shadow-default dark:border-strokedark dark:bg-boxdark">
        <SpinnerLoader />
      </div>
    );
  }

  return (
    <div className="c-meta-field c-meta-field_v2 mt-20 rounded-sm border border-stroke bg-white shadow-default dark:border-strokedark dark:bg-boxdark">
      <div className="border-b border-stroke px-7 py-4 dark:border-strokedark">
        <h3 className="font-medium text-black dark:text-white">{title}</h3>
      </div>
      <Formik
        initialValues={{ metaFields: fields }}
        validationSchema={validationSchema}
        onSubmit={handleUpdateAll}
        enableReinitialize
      >
        {({ values, errors, isSubmitting, setFieldValue }) => (
          <Form className="p-7">
            <FieldArray name="metaFields">
              {({ remove, push }) => (
                <>
                  <div className="flex flex-col gap-5.5">
                    {values.metaFields.length === 0 ? (
                      <p className="text-gray-500 text-sm">{t('labels.noMetaFields')}</p>
                    ) : (
                      <>
                        {values.metaFields.map((metaFieldItem, index) => (
                          <FieldInputV2
                            key={metaFieldItem.id || `field-${index}`}
                            field={{ value: metaFieldItem.value, key: metaFieldItem.key }}
                            onChange={(updatedField) => {
                              setFieldValue(`metaFields[${index}].key`, updatedField.key);
                              setFieldValue(`metaFields[${index}].value`, updatedField.value);
                            }}
                            onRemove={async () => {
                              await deleteMetaField({ id, metaFieldId: metaFieldItem.id });
                              remove(index);
                              onChangeListMetaFields(values.metaFields.filter((_, i) => i !== index));
                            }}
                            keyError={errors?.metaFields?.[index]?.['key']}
                            valueError={errors?.metaFields?.[index]?.['value']}
                          />
                        ))}
                        <button
                          type="submit"
                          className="w-fit rounded-md bg-primary px-4 py-2 text-center font-medium text-white hover:bg-opacity-90 disabled:opacity-50"
                          disabled={isViewLoading || isSubmitting || !!errors.metaFields}
                        >
                          {isSubmitting ? t('buttons.updating') : t('buttons.update')}
                        </button>
                      </>
                    )}
                  </div>

                  <NewFieldForm
                    isLoading={isViewLoading}
                    range={{ min: RANGE_META_FIELD.MIN, max: RANGE_META_FIELD.MAX }}
                    onAdd={async (newField) => {
                      setIsLoading(true);
                      try {
                        const createdMetaField = await createMetaField({ id, data: newField });
                        push(createdMetaField);
                        onChangeListMetaFields([...values.metaFields, createdMetaField]);
                      } catch (error) {
                        console.error('Failed to create meta field:', error);
                      } finally {
                        setIsLoading(false);
                      }
                    }}
                  />

                  <div className="mt-5.5 flex gap-4">
                    {buttons && buttons.map((child, index) => {
                      if (React.isValidElement<{ disabled?: boolean }>(child)) {
                        return React.cloneElement(child, {
                          key: index,
                          disabled: isViewLoading || isSubmitting,
                        });
                      }
                      return child;
                    })}
                  </div>
                </>
              )}
            </FieldArray>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default MetaFieldsV2;
